import { unwrapApiPayload } from "./mapUserActivityGraph";

function getActivityLogList(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;

  const payload = unwrapApiPayload(response);
  if (Array.isArray(payload)) return payload;

  const candidates = [
    response?.activityLogs,
    response?.activitylogs,
    response?.activityLog,
    response?.activitylog,
    response?.data?.activityLogs,
    response?.data?.activitylogs,
    response?.data?.activityLog,
    response?.data?.activitylog,
    response?.data?.logs,
    response?.logs,
    response?.data,
    payload?.activityLogs,
    payload?.activitylogs,
    payload?.activityLog,
    payload?.activitylog,
    payload?.logs,
    payload?.data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

export function parseActivityDate(item) {
  const raw =
    item?.createdAt ||
    item?.created_at ||
    item?.timestamp ||
    item?.date ||
    item?.time ||
    item?.updatedAt ||
    item?.updated_at;

  if (raw == null || raw === "") return null;

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatRelativeTime(value) {
  const date = value instanceof Date ? value : parseActivityDate({ createdAt: value });
  if (!date) return "-";

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return "Just now";

  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffSecs < 60) return "Just now";
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getInitials(item) {
  const source =
    item.userName ||
    item.user?.name ||
    item.name ||
    item.userRole ||
    item.role ||
    item.action ||
    "A";

  const words = String(source).trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  return String(source).slice(0, 2).toUpperCase();
}

function sortByNewest(items) {
  return [...items].sort((a, b) => {
    const dateA = parseActivityDate(a) || new Date(0);
    const dateB = parseActivityDate(b) || new Date(0);
    if (dateB - dateA !== 0) return dateB - dateA;

    const idA = String(a?._id || a?.id || "");
    const idB = String(b?._id || b?.id || "");
    return idB.localeCompare(idA);
  });
}

export function mapActivityItems(response) {
  const list = sortByNewest(getActivityLogList(response));

  return list.map((item, index) => {
    const activityDate = parseActivityDate(item);

    return {
      id: item._id || item.id || `activity-${index}`,
      action: item.action || "-",
      description: item.description || "-",
      time: formatRelativeTime(activityDate),
      createdAt: activityDate,
      initials: getInitials(item),
    };
  });
}
